/**
 * EmailBodyEditor
 *
 * Rich-text email editor powered by TipTap.
 * Supports bold, italic, lists, and links.
 * Stores and emits HTML strings.
 *
 * Follows TipTap's conventional patterns:
 * - useEditor for editor instance
 * - useEditorState with selector for performant toolbar state
 * - Separate MenuBar component receiving editor as prop
 * - Command chaining: editor.chain().focus().command().run()
 */
import type { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  EditorContent,
  type EditorStateSnapshot,
  useEditor,
  useEditorState,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
} from 'lucide-react';
import { useEffect } from 'react';

import { cn } from '@/lib/utils';

// ── Toolbar state selector ─────────────────────────────────────────────────

const menuBarSelector = (ctx: EditorStateSnapshot<Editor>) => ({
  isBold: ctx.editor.isActive('bold'),
  canBold: ctx.editor.can().chain().focus().toggleBold().run(),
  isItalic: ctx.editor.isActive('italic'),
  canItalic: ctx.editor.can().chain().focus().toggleItalic().run(),
  isBulletList: ctx.editor.isActive('bulletList'),
  isOrderedList: ctx.editor.isActive('orderedList'),
  isLink: ctx.editor.isActive('link'),
});

// ── MenuBar ─────────────────────────────────────────────────────────────────

const MenuBar = ({
  editor,
  label,
}: {
  editor: Editor;
  label?: string;
}) => {
  const state = useEditorState({ editor, selector: menuBarSelector });

  const handleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    // eslint-disable-next-line no-alert
    const url = window.prompt('Enter link URL');
    if (!url) return;
    editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="flex shrink-0 items-center justify-between">
      {label && (
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          {label}
        </span>
      )}
      <div className="ml-auto flex items-center gap-0.5">
        <ToolbarButton
          icon={<Bold className="h-3 w-3" />}
          label="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!state.canBold}
          isActive={state.isBold}
        />
        <ToolbarButton
          icon={<Italic className="h-3 w-3" />}
          label="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!state.canItalic}
          isActive={state.isItalic}
        />
        <ToolbarButton
          icon={<List className="h-3 w-3" />}
          label="Bulleted list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={state.isBulletList}
        />
        <ToolbarButton
          icon={<ListOrdered className="h-3 w-3" />}
          label="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={state.isOrderedList}
        />
        <ToolbarButton
          icon={<LinkIcon className="h-3 w-3" />}
          label="Insert link"
          onClick={handleLink}
          isActive={state.isLink}
        />
      </div>
    </div>
  );
};

// ── Toolbar button ──────────────────────────────────────────────────────────

const ToolbarButton = ({
  icon,
  label,
  onClick,
  isActive = false,
  disabled = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'inline-flex items-center justify-center rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300',
      isActive &&
        'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
      disabled && 'opacity-40 pointer-events-none',
    )}
    aria-label={label}
  >
    {icon}
  </button>
);

// ── Main editor ─────────────────────────────────────────────────────────────

interface EmailBodyEditorProps {
  value: string;
  onChange: (next: string) => void;
  /** Optional label shown above the toolbar. */
  label?: string;
  /** Placeholder text shown when editor is empty. */
  placeholder?: string;
  /** Additional class names for the editor container. */
  className?: string;
}

export const EmailBodyEditor = ({
  value,
  onChange,
  label,
  placeholder = 'Write your email...',
  className,
}: EmailBodyEditorProps) => {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-blue-600 underline cursor-pointer' },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-full',
      },
    },
  });

  // Sync external value → editor (e.g. when draft loads from API)
  useEffect(() => {
    if (editor && editor.getHTML() !== (value || '')) {
      editor.commands.setContent(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-col gap-1">
      <MenuBar editor={editor} label={label} />
      <EditorContent
        editor={editor}
        className={cn(
          'min-h-[120px] rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2.5 py-2 text-xs font-medium text-zinc-800 dark:text-zinc-200',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-full',
          className,
        )}
      />
    </div>
  );
};
